import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Search, ExternalLink, Check, X, Star, Info } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { PublicShell, Loading, EmptyState } from "@/components/shared";
import api from "@/lib/api";

const CATS = ["all", "AI", "Marketing", "E-commerce", "Finance", "Operations"];

export default function Tools() {
  const [search, setSearch] = useState("");
  const [cat, setCat] = useState("all");
  const [selected, setSelected] = useState(null);
  const params = {};
  if (search) params.search = search;
  if (cat !== "all") params.category = cat;
  const { data, isLoading } = useQuery({ queryKey: ["tools", params], queryFn: async () => (await api.get("/tools", { params })).data });

  const visit = async (tool) => {
    try { await api.post(`/tools/${tool.id}/click`); } catch {}
    window.open(tool.affiliate_link || "#", "_blank");
  };

  return (
    <PublicShell>
      <section className="bg-navy grain"><div className="relative mx-auto max-w-7xl px-5 py-16 lg:px-8">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-emerald">CEO Tool Stack</p>
        <h1 className="mt-3 font-heading text-4xl font-bold tracking-tight text-white sm:text-5xl">The tools we actually recommend</h1>
        <p className="mt-3 max-w-2xl text-slate-400">Honest reviews, pricing summaries and setup guides for the software Indian founders use to grow.</p>
      </div></section>

      <section className="mx-auto max-w-7xl px-5 py-8 lg:px-8">
        <div className="mb-8 flex flex-col gap-3 sm:flex-row">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search tools…" data-testid="tools-search" className="h-11 pl-10" />
          </div>
          <Select value={cat} onValueChange={setCat}>
            <SelectTrigger className="h-11 w-full sm:w-[180px]" data-testid="tools-category"><SelectValue /></SelectTrigger>
            <SelectContent>{CATS.map((c) => <SelectItem key={c} value={c}>{c === "all" ? "All categories" : c}</SelectItem>)}</SelectContent>
          </Select>
        </div>

        {isLoading ? <Loading /> : (data?.length ? (
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {data.map((t) => (
              <div key={t.id} className="flex flex-col rounded-xl border border-slate-200 bg-white p-5" data-testid={`tool-card-${t.id}`}>
                <div className="flex items-start justify-between">
                  <div className="inline-flex h-11 w-11 items-center justify-center rounded-lg bg-navy font-heading font-bold text-white">{t.name[0]}</div>
                  {t.featured && <span className="inline-flex items-center gap-1 rounded bg-gold px-2 py-1 text-[11px] font-bold text-navy"><Star className="h-3 w-3" strokeWidth={2.5} /> Featured</span>}
                </div>
                <h3 className="mt-4 font-heading text-lg font-semibold text-navy">{t.name}</h3>
                <p className="text-xs font-semibold uppercase tracking-wider text-emerald">{t.category}</p>
                <p className="mt-2 flex-1 text-sm text-slate-600">{t.description}</p>
                <p className="mt-3 text-xs text-slate-500"><span className="font-semibold text-navy">Pricing:</span> {t.pricing_summary}</p>
                <div className="mt-4 flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => setSelected(t)} className="flex-1 rounded-md" data-testid={`tool-details-${t.id}`}>Details</Button>
                  <Button size="sm" onClick={() => visit(t)} className="flex-1 rounded-md bg-emerald text-white hover:bg-emerald-700" data-testid={`tool-visit-${t.id}`}>Visit <ExternalLink className="ml-1 h-3.5 w-3.5" /></Button>
                </div>
              </div>
            ))}
          </div>
        ) : <EmptyState title="No tools found" subtitle="Try a different search or category." />)}
      </section>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-lg" data-testid="tool-dialog">
          {selected && (
            <>
              <DialogHeader><DialogTitle className="font-heading text-2xl">{selected.name}</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <p className="text-sm text-slate-600">{selected.description}</p>
                <div className="rounded-lg bg-slate-50 p-3 text-sm"><span className="font-semibold text-navy">Best for:</span> <span className="text-slate-600">{selected.best_for}</span></div>
                <div className="grid grid-cols-2 gap-4">
                  <div><p className="mb-2 text-xs font-bold uppercase tracking-wider text-emerald">Pros</p><ul className="space-y-1.5">{selected.pros?.map((p, i) => <li key={i} className="flex items-start gap-1.5 text-sm text-slate-600"><Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-emerald" /> {p}</li>)}</ul></div>
                  <div><p className="mb-2 text-xs font-bold uppercase tracking-wider text-red-500">Cons</p><ul className="space-y-1.5">{selected.cons?.map((p, i) => <li key={i} className="flex items-start gap-1.5 text-sm text-slate-600"><X className="mt-0.5 h-4 w-4 flex-shrink-0 text-red-500" /> {p}</li>)}</ul></div>
                </div>
                {selected.recommendation && <div className="rounded-lg border border-emerald/20 bg-emerald/5 p-3 text-sm text-slate-700"><span className="font-semibold text-emerald">CEO Playbook take: </span>{selected.recommendation}</div>}
                <Button onClick={() => visit(selected)} className="w-full rounded-md bg-navy text-white hover:bg-navysurface" data-testid="tool-dialog-visit">Visit {selected.name} <ExternalLink className="ml-1.5 h-4 w-4" /></Button>
                <p className="flex items-start gap-1.5 text-xs text-slate-400"><Info className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" /> This may be an affiliate link. We may earn a commission at no extra cost to you. We only recommend tools we believe in.</p>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </PublicShell>
  );
}
